import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";
import { useLogin } from "../login/login";
import {
    ICrmActivity,
    ICrmBlocker,
    ICrmBlockerDef,
    ICrmBlockerRollup,
    ICrmError,
    ICrmLead,
    ICrmInboxCounts,
    ICrmLeadDetail,
    ICrmAccountPeople,
    ICrmArchiveResult,
    CrmArchiveReason,
    ICrmLeadBookmark,
    ICrmLeadEnrichment,
    ILeadCommerce,
    ICrmLeadFilters,
    ICrmLeadListResult,
    ICrmStage
} from "./types";

const crmUrl = `${environment.v2Url}/crm`;

export type CrmLeadsKeyFunctions = {
    listLeads: (filters: ICrmLeadFilters) => Promise<ICrmLeadListResult | ICrmError>;
    listBlockerTypes: () => Promise<ICrmBlockerDef[] | ICrmError>;
    listBlockers: (id: number) => Promise<ICrmBlocker[] | ICrmError>;
    /** Archive leads (spam, duplicates…). block_login is manager-only. */
    archiveLeads: (ids: number[], opts: { reason: CrmArchiveReason; note?: string; block_login?: boolean }) => Promise<ICrmArchiveResult | ICrmError>;
    restoreLeads: (ids: number[]) => Promise<{ restored: number; unblocked_logins: number } | ICrmError>;
    loginBlocked: (id: number) => Promise<{ blocked: boolean } | ICrmError>;
    /** Managers: score every live lead for spam. Only tags; never archives. */
    rescoreSpam: () => Promise<{ scanned: number; flagged: number } | ICrmError>;
    /** Everyone's bookmarks on a lead. Set and remove act on yours, and answer with the new list. */
    listBookmarks: (id: number) => Promise<ICrmLeadBookmark[] | ICrmError>;
    setBookmark: (id: number, headline: string | null) => Promise<ICrmLeadBookmark[] | ICrmError>;
    removeBookmark: (id: number) => Promise<ICrmLeadBookmark[] | ICrmError>;
    /** Unconfirmed finds about a lead. Every change answers with the whole box again. */
    getEnrichment: (id: number) => Promise<ICrmLeadEnrichment | ICrmError>;
    refreshEnrichment: (id: number) => Promise<ICrmLeadEnrichment | ICrmError>;
    reviewEnrichment: (id: number, action: 'accept' | 'dismiss' | 'restore', ids: number[]) => Promise<ICrmLeadEnrichment | ICrmError>;
    acceptAllEnrichment: (id: number) => Promise<ICrmLeadEnrichment | ICrmError>;
    /** Profile pictures for the lead's profiles and suggested profiles, keyed by the profile value. */
    getAvatars: (id: number) => Promise<{ avatars: Record<string, string> } | ICrmError>;
    setBlocker: (id: number, tag: string, weight: number, journey?: string | null, note?: string | null) => Promise<ICrmBlocker[] | ICrmError>;
    resolveBlocker: (id: number, tag: string, resolved: boolean) => Promise<ICrmBlocker[] | ICrmError>;
    removeBlocker: (id: number, tag: string) => Promise<ICrmBlocker[] | ICrmError>;
    blockerRollup: (journey?: string | null) => Promise<ICrmBlockerRollup[] | ICrmError>;
    getLead: (id: number) => Promise<ICrmLeadDetail | ICrmError>;
    getInboxCounts: () => Promise<ICrmInboxCounts | ICrmError>;
    /** Last N days (null = all time), or a from–to window (ISO, to exclusive) when range is given. */
    getCommerce: (id: number, days: number | null, range?: { from: string; to: string }) => Promise<ILeadCommerce | ICrmError>;
    /** Everyone with a login on the lead's account. */
    getAccountPeople: (id: number) => Promise<ICrmAccountPeople | ICrmError>;
    listStages: () => Promise<ICrmStage[] | ICrmError>;
    claimLead: (id: number) => Promise<ICrmLead | ICrmError>;
    assignLead: (id: number, ownerUserId: number | null) => Promise<ICrmLead | ICrmError>;
    setStage: (id: number, stageId: number) => Promise<ICrmLead | ICrmError>;
    setLifecycle: (id: number, lifecycle: string, lostReason?: string) => Promise<ICrmLead | ICrmError>;
    setDisposition: (id: number, disposition: string, reason?: string) => Promise<ICrmLead | ICrmError>;
    setSocials: (id: number, socials: Record<string, string>) => Promise<ICrmLead | ICrmError>;
    setPlatform: (id: number, platform: string | null) => Promise<ICrmLead | ICrmError>;
    setWebsite: (id: number, website: string | null) => Promise<ICrmLead | ICrmError>;
    listActivities: (id: number) => Promise<ICrmActivity[] | ICrmError>;
    addActivity: (id: number, type: string, body?: string) => Promise<ICrmActivity | ICrmError>;
};

/**
 * Follows the artist_influencers convention rather than `errorCatcher`: every
 * method resolves either the payload or a typed ICrmError and never throws, so
 * callers branch on the result instead of wrapping each call in try/catch.
 * A 401 still logs out, matching the shared catchers.
 */
export const useCrmLeads = (): [CrmLeadsKeyFunctions] => {
    const [, authToken, loginFunctions] = useLogin();
    const headers = { 'X-Wallmates-Auth': authToken };

    const handle = (err: any): ICrmError => {
        const status = err?.response?.status ?? 0;
        if (status === 401) loginFunctions.logout();
        return {
            ok: false,
            status,
            message: err?.response?.data?.message
                || (status === 0 ? 'Could not reach Wallmates. Check your connection.' : 'Something went wrong.')
        };
    };

    const listLeads = async (filters: ICrmLeadFilters): Promise<ICrmLeadListResult | ICrmError> => {
        try {
            // Empty-string filter values mean "no filter"; sending them would
            // match literally and return nothing.
            const params: Record<string, string | number> = {};
            if (filters.view) params.view = filters.view;
            if (filters.journey) params.journey = filters.journey;
            if (filters.disposition) params.disposition = filters.disposition;
            if (filters.owner) params.owner = filters.owner;
            if (filters.minScore) params.minScore = filters.minScore;
            if (filters.q) params.q = filters.q;
            if (filters.sort) params.sort = filters.sort;
            if (filters.usage) params.usage = filters.usage;
            if (filters.bookmarks) params.bookmarks = filters.bookmarks;
            if (filters.view === 'demoed' && filters.demo) params.demo = filters.demo;
            params.page = filters.page ?? 0;
            if (filters.limit) params.limit = filters.limit;

            const result: AxiosResponse<ICrmLeadListResult> =
                await axios.get(`${crmUrl}/leads`, { headers, params });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const getInboxCounts = async (): Promise<ICrmInboxCounts | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmInboxCounts> = await axios.get(`${crmUrl}/leads/counts`, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const getLead = async (id: number): Promise<ICrmLeadDetail | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmLeadDetail> = await axios.get(`${crmUrl}/leads/${id}`, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const getCommerce = async (id: number, days: number | null, range?: { from: string; to: string }): Promise<ILeadCommerce | ICrmError> => {
        try {
            const params = range ? { from: range.from, to: range.to } : (days ? { days } : { days: 'all' });
            const result: AxiosResponse<ILeadCommerce> = await axios.get(`${crmUrl}/leads/${id}/commerce`, { headers, params });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const getAccountPeople = async (id: number): Promise<ICrmAccountPeople | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmAccountPeople> = await axios.get(`${crmUrl}/leads/${id}/people`, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const listStages = async (): Promise<ICrmStage[] | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmStage[]> = await axios.get(`${crmUrl}/stages`, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const claimLead = async (id: number): Promise<ICrmLead | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmLead> =
                await axios.post(`${crmUrl}/leads/${id}/claim`, {}, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const assignLead = async (id: number, ownerUserId: number | null): Promise<ICrmLead | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmLead> =
                await axios.post(`${crmUrl}/leads/${id}/assign`, { owner_user_id: ownerUserId }, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const setStage = async (id: number, stageId: number): Promise<ICrmLead | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmLead> =
                await axios.post(`${crmUrl}/leads/${id}/stage`, { stage_id: stageId }, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const setLifecycle = async (id: number, lifecycle: string, lostReason?: string): Promise<ICrmLead | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmLead> = await axios.post(
                `${crmUrl}/leads/${id}/lifecycle`,
                { lifecycle, lost_reason: lostReason },
                { headers }
            );
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const setDisposition = async (id: number, disposition: string, reason?: string): Promise<ICrmLead | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmLead> = await axios.post(
                `${crmUrl}/leads/${id}/disposition`,
                { disposition, reason },
                { headers }
            );
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const setSocials = async (id: number, socials: Record<string, string>): Promise<ICrmLead | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmLead> = await axios.post(`${crmUrl}/leads/${id}/socials`, { socials }, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const setPlatform = async (id: number, platform: string | null): Promise<ICrmLead | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmLead> = await axios.post(`${crmUrl}/leads/${id}/platform`, { platform }, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const setWebsite = async (id: number, website: string | null): Promise<ICrmLead | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmLead> = await axios.post(`${crmUrl}/leads/${id}/website`, { website }, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const listActivities = async (id: number): Promise<ICrmActivity[] | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmActivity[]> =
                await axios.get(`${crmUrl}/leads/${id}/activities`, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const addActivity = async (id: number, type: string, body?: string): Promise<ICrmActivity | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmActivity> =
                await axios.post(`${crmUrl}/leads/${id}/activities`, { type, body }, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    // #region blockers
    const listBlockerTypes = async (): Promise<ICrmBlockerDef[] | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmBlockerDef[]> = await axios.get(`${crmUrl}/blockers`, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const bookmarkUrl = (id: number, one: boolean) => crmUrl + '/leads/' + id + (one ? '/bookmark' : '/bookmarks');
    const listBookmarks = async (id: number): Promise<ICrmLeadBookmark[] | ICrmError> => {
        try { return (await axios.get<ICrmLeadBookmark[]>(bookmarkUrl(id, false), { headers })).data; } catch (err: any) { return handle(err); }
    };
    const setBookmark = async (id: number, headline: string | null): Promise<ICrmLeadBookmark[] | ICrmError> => {
        try { return (await axios.put<ICrmLeadBookmark[]>(bookmarkUrl(id, true), { headline }, { headers })).data; } catch (err: any) { return handle(err); }
    };
    const removeBookmark = async (id: number): Promise<ICrmLeadBookmark[] | ICrmError> => {
        try { return (await axios.delete<ICrmLeadBookmark[]>(bookmarkUrl(id, true), { headers })).data; } catch (err: any) { return handle(err); }
    };

    const enrichUrl = (id: number, tail = '') => crmUrl + '/leads/' + id + '/enrichment' + tail;
    const getEnrichment = async (id: number): Promise<ICrmLeadEnrichment | ICrmError> => {
        try { return (await axios.get<ICrmLeadEnrichment>(enrichUrl(id), { headers })).data; } catch (err: any) { return handle(err); }
    };
    const refreshEnrichment = async (id: number): Promise<ICrmLeadEnrichment | ICrmError> => {
        try { return (await axios.post<ICrmLeadEnrichment>(enrichUrl(id, '/refresh'), {}, { headers, timeout: 90_000 })).data; } catch (err: any) { return handle(err); }
    };
    const reviewEnrichment = async (id: number, action: 'accept' | 'dismiss' | 'restore', ids: number[]): Promise<ICrmLeadEnrichment | ICrmError> => {
        try { return (await axios.post<ICrmLeadEnrichment>(enrichUrl(id, '/' + action), { ids }, { headers, timeout: 90_000 })).data; } catch (err: any) { return handle(err); }
    };
    const acceptAllEnrichment = async (id: number): Promise<ICrmLeadEnrichment | ICrmError> => {
        try { return (await axios.post<ICrmLeadEnrichment>(enrichUrl(id, '/accept-all'), {}, { headers, timeout: 90_000 })).data; } catch (err: any) { return handle(err); }
    };
    const getAvatars = async (id: number): Promise<{ avatars: Record<string, string> } | ICrmError> => {
        // The first look at a lead fetches each profile page, so allow it time.
        try { return (await axios.get<{ avatars: Record<string, string> }>(crmUrl + '/leads/' + id + '/avatars', { headers, timeout: 60_000 })).data; } catch (err: any) { return handle(err); }
    };

    const archiveLeads = async (ids: number[], opts: { reason: CrmArchiveReason; note?: string; block_login?: boolean }): Promise<ICrmArchiveResult | ICrmError> => {
        try { return (await axios.post<ICrmArchiveResult>(crmUrl + '/leads/archive', { lead_ids: ids, ...opts }, { headers })).data; } catch (err: any) { return handle(err); }
    };
    const restoreLeads = async (ids: number[]): Promise<{ restored: number; unblocked_logins: number } | ICrmError> => {
        try { return (await axios.post<{ restored: number; unblocked_logins: number }>(crmUrl + '/leads/restore', { lead_ids: ids }, { headers })).data; } catch (err: any) { return handle(err); }
    };
    const rescoreSpam = async (): Promise<{ scanned: number; flagged: number } | ICrmError> => {
        try { return (await axios.post<{ scanned: number; flagged: number }>(crmUrl + '/leads/spam-rescore', {}, { headers })).data; } catch (err: any) { return handle(err); }
    };
    const loginBlocked = async (id: number): Promise<{ blocked: boolean } | ICrmError> => {
        try { return (await axios.get<{ blocked: boolean }>(crmUrl + '/leads/' + id + '/login-block', { headers })).data; } catch (err: any) { return handle(err); }
    };

    const listBlockers = async (id: number): Promise<ICrmBlocker[] | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmBlocker[]> = await axios.get(`${crmUrl}/leads/${id}/blockers`, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const setBlocker = async (
        id: number, tag: string, weight: number, journey?: string | null, note?: string | null
    ): Promise<ICrmBlocker[] | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmBlocker[]> =
                await axios.post(`${crmUrl}/leads/${id}/blockers`, { tag, weight, journey, note }, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const resolveBlocker = async (id: number, tag: string, resolved: boolean): Promise<ICrmBlocker[] | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmBlocker[]> =
                await axios.post(`${crmUrl}/leads/${id}/blockers/${tag}/resolve`, { resolved }, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const removeBlocker = async (id: number, tag: string): Promise<ICrmBlocker[] | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmBlocker[]> =
                await axios.delete(`${crmUrl}/leads/${id}/blockers/${tag}`, { headers });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const blockerRollup = async (journey?: string | null): Promise<ICrmBlockerRollup[] | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmBlockerRollup[]> =
                await axios.get(`${crmUrl}/blockers/rollup`, { headers, params: journey ? { journey } : {} });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    return [{
        listBlockerTypes, listBlockers, setBlocker, resolveBlocker, removeBlocker, blockerRollup,
        listLeads, getInboxCounts, getLead, getCommerce, getAccountPeople, listBookmarks, setBookmark, removeBookmark, getEnrichment, refreshEnrichment, reviewEnrichment, acceptAllEnrichment, getAvatars, archiveLeads, restoreLeads, loginBlocked, rescoreSpam, listStages, claimLead, assignLead,
        setStage, setLifecycle, setDisposition, setSocials, setPlatform, setWebsite, listActivities, addActivity
    }];
};
